/**
 * D-EZ-110 — ERAS, DYNASTIES AND THE LEAGUE'S TIMELINE.
 *
 * A league with history talks about it in eras: "the Ballew years", "when
 * Waddell Brothers won everything", "the wide-open seasons". Eras here are
 * NAMED BY RULE from the champions' list, so the same history always gets the
 * same names, and an era can be argued with by looking at the list:
 *
 *   • A DRIVER'S ERA — two or more of his titles, each within three seasons of
 *     the last: "The Ballew Years, seasons 3–6" (three titles or more: "Era").
 *   • A SHOP'S DYNASTY — three or more titles for one charter (a sold charter
 *     is the same charter) within the same spacing, or two by different men:
 *     "The Waddell Brothers Dynasty", "Waddell Brothers' Golden Run". A shop
 *     whose titles were all one man's is his era, not a second band.
 *   • THE OPEN YEARS — three or more settled seasons in a row inside nobody's
 *     era, with a different champion every time.
 *
 * The timeline gives every season its champion, its era bands and the
 * storyline that defined it, in a fixed order of precedence: a record broken,
 * then the hottest rivalry of the year, then the most dominant season.
 */

import { seasonAwards } from '../history/index.ts';
import { recordsBrokenIn } from '../story/yearInReview.ts';
import { dollars } from './text.ts';
import { asHistory, chapterContext, seasonTeam, teamBase, type ChapterSeason } from './league.ts';
import { surname, tell, count, ordinal, rotor, type Told } from './text.ts';

export type EraKind = 'driver' | 'shop' | 'open';

export interface Era {
  key: string;
  kind: EraKind;
  title: Told;
  from: number;
  to: number;
  /** The era's last season is the latest settled one. */
  ongoing: boolean;
  driverId: string | null;
  teamBase: string | null;
  /** The title seasons that made it (empty for the open years). */
  titles: number[];
  line: Told;
}

export interface TimelineSeason {
  seasonNumber: number;
  complete: boolean;
  champion: { driverId: string; name: string; titlesToDate: number; team: string | null } | null;
  eras: string[];
  storyline: Told | null;
  also: Told[];
}

export interface LeagueTimeline {
  eras: Era[];
  seasons: TimelineSeason[];
}

const GAP = 3;

function clusters(seasons: number[]): number[][] {
  const out: number[][] = [];
  for (const n of [...seasons].sort((a, b) => a - b)) {
    const cur = out[out.length - 1];
    if (cur && n - cur[cur.length - 1] <= GAP) cur.push(n);
    else out.push([n]);
  }
  return out;
}

const span = (a: number, b: number) => (a === b ? `season ${a}` : `seasons ${a}–${b}`);

/** The league's eras, oldest first, from the settled seasons only. */
export function leagueEras(input: readonly ChapterSeason[]): Era[] {
  const settled = asHistory(input)
    .filter((s) => s.complete !== false && s.season.championId)
    .sort((a, b) => a.seasonNumber - b.seasonNumber) as unknown as ChapterSeason[];
  if (settled.length === 0) return [];
  const latest = settled[settled.length - 1].seasonNumber;
  const champs = settled.map((s) => {
    const id = s.season.championId as string;
    const t = seasonTeam(s, id);
    return { n: s.seasonNumber, id, name: s.season.finalStandings.byDriver[id]?.name ?? id, team: t ? teamBase(t.teamId) : null, teamName: t?.name ?? null };
  });
  const eras: Era[] = [];
  // Drivers.
  const byDriver = new Map<string, typeof champs>();
  for (const c of champs) byDriver.set(c.id, [...(byDriver.get(c.id) ?? []), c]);
  for (const [id, list] of byDriver) {
    const groups = clusters(list.map((c) => c.n)).filter((g) => g.length >= 2);
    groups.forEach((g, i) => {
      const name = list[0].name;
      const from = g[0];
      const to = g[g.length - 1];
      const slots = { sur: surname(name), name, span: span(from, to), k: count(g.length) };
      const title = g.length >= 3 ? tell(i === 0 ? 'The {sur} Era' : 'The {sur} Era, Again', slots) : tell(i === 0 ? 'The {sur} Years' : 'The {sur} Years, Again', slots);
      eras.push({
        key: `driver|${id}|${from}`,
        kind: 'driver',
        title,
        from,
        to,
        ongoing: to === latest,
        driverId: id,
        teamBase: null,
        titles: g,
        line: tell('{name} won {k} titles in {span}.', slots),
      });
    });
  }
  // Shops.
  const byShop = new Map<string, typeof champs>();
  for (const c of champs) if (c.team) byShop.set(c.team, [...(byShop.get(c.team) ?? []), c]);
  for (const [base, list] of byShop) {
    for (const g of clusters(list.map((c) => c.n))) {
      const inG = list.filter((c) => g.includes(c.n));
      const men = new Set(inG.map((c) => c.id));
      if (g.length < 2 || men.size < 2) {
        if (!(g.length >= 3 && men.size >= 2)) continue;
      }
      const team = inG[inG.length - 1].teamName ?? base;
      const from = g[0];
      const to = g[g.length - 1];
      const slots = { team, span: span(from, to), k: count(g.length), men: count(men.size) };
      eras.push({
        key: `shop|${base}|${from}`,
        kind: 'shop',
        title: g.length >= 3 ? tell('The {team} Dynasty', slots) : tell('{teams} Golden Run', { ...slots, teams: /s$/.test(team) ? `${team}’` : `${team}’s` }),
        from,
        to,
        ongoing: to === latest,
        driverId: null,
        teamBase: base,
        titles: g,
        line: tell('{team} won {k} titles with {men} different drivers in {span}.', slots),
      });
    }
  }
  // The open years: every run of three or more seasons in a row inside nobody's era.
  const covered = new Set<number>();
  for (const e of eras) if (e.kind === 'driver') for (let n = e.from; n <= e.to; n++) covered.add(n);
  let run: typeof champs = [];
  const flush = () => {
    if (run.length >= 3) {
      const from = run[0].n;
      const to = run[run.length - 1].n;
      const distinct = new Set(run.map((c) => c.id)).size;
      const slots = { span: span(from, to), k: count(run.length), d: count(distinct) };
      eras.push({
        key: `open|${from}`,
        kind: 'open',
        title: tell(eras.some((e) => e.kind === 'open') ? 'Wide Open Again' : 'The Open Years', slots),
        from,
        to,
        ongoing: to === latest,
        driverId: null,
        teamBase: null,
        titles: [],
        line: distinct === run.length ? tell('{k} seasons, {k} different champions: {span}.', slots) : tell('{k} seasons and {d} different champions, nobody dominant: {span}.', slots),
      });
    }
    run = [];
  };
  for (const c of champs) {
    const prev = run[run.length - 1];
    if (covered.has(c.n) || (prev && c.n !== prev.n + 1)) {
      flush();
      if (!covered.has(c.n)) run.push(c);
    } else run.push(c);
  }
  flush();
  return eras.sort((a, b) => a.from - b.from || kindOrder(a.kind) - kindOrder(b.kind) || a.key.localeCompare(b.key));
}

const kindOrder = (k: EraKind) => (k === 'driver' ? 0 : k === 'shop' ? 1 : 2);

/** Season by season: champion, eras, and the storyline that defined the year. */
export function leagueTimeline(input: readonly ChapterSeason[]): LeagueTimeline {
  const seasons = [...input].sort((a, b) => a.seasonNumber - b.seasonNumber);
  const eras = leagueEras(seasons);
  const cx = chapterContext(seasons);
  const reg = cx.registry;
  const book = cx.people.book;
  const out: TimelineSeason[] = [];
  const rivR = rotor(`timeline-riv|${cx.leagueKey}`);
  for (const s of seasons) {
    const complete = s.complete !== false;
    const n = s.seasonNumber;
    const champId = complete ? s.season.championId : null;
    let champion: TimelineSeason['champion'] = null;
    if (champId) {
      const titlesToDate = (reg.byId[champId]?.bySeason ?? []).filter((l) => l.champion && l.seasonNumber <= n).length;
      champion = { driverId: champId, name: s.season.finalStandings.byDriver[champId]?.name ?? reg.byId[champId]?.name ?? champId, titlesToDate, team: seasonTeam(s, champId)?.name ?? null };
    }
    const story: Told[] = [];
    const also: Told[] = [];
    const recs = complete ? recordsBrokenIn(asHistory(seasons), n) : [];
    const recLine = (rec: (typeof recs)[number]) =>
      tell('{name} broke the record — {what}: {value}.', { name: rec.name, what: rec.title.toLowerCase(), value: /won|winnings/i.test(rec.title) ? dollars(rec.value) : String(rec.value) });
    // 1. A single-season record.
    const seasonRec = recs.find((r) => r.scope === 'season');
    if (seasonRec) story.push(recLine(seasonRec));
    // 2. The hottest rivalry of the year.
    let hot: { a: string; b: string; heat: number; worst: string | null } | null = null;
    for (const r of book.byPair.values()) {
      const ms = r.moments.filter((m) => m.seasonNumber === n);
      if (ms.length === 0) continue;
      const heat = ms.reduce((t, m) => t + m.heat, 0);
      if (!hot || heat > hot.heat) {
        const worst = [...ms].sort((x, y) => y.heat - x.heat)[0];
        hot = { a: r.aName, b: r.bName, heat, worst: worst?.text ?? null };
      }
    }
    if (hot && hot.heat >= 11) {
      story.push(
        hot.worst
          ? rivR(['The feud of the year: {a} and {b}. {worst}', '{a} against {b} was the rivalry of the season. {worst}', 'Nobody ran hotter than {a} and {b}. {worst}', 'The year belonged, in its way, to {a} and {b}. {worst}'], { a: hot.a, b: hot.b, worst: hot.worst })
          : tell('The feud of the year: {a} and {b}.', { a: hot.a, b: hot.b }),
      );
    }
    // 3. A career record changing hands.
    const careerRec = recs.find((r) => r.scope === 'career');
    if (careerRec) story.push(recLine(careerRec));
    if (complete) {
      // 4. The most dominant season.
      const top = [...s.season.finalStandings.drivers].sort((a, b) => b.wins - a.wins || a.rank - b.rank)[0];
      if (top && top.wins >= 5) story.push(tell('{name} won {w} of the {r} races.', { name: top.name, w: top.wins, r: s.season.races.length }));
      if (champion && champion.titlesToDate === 1) also.push(tell('A first title for {name}.', { name: champion.name }));
      else if (champion) {
        const prev = seasons.find((x) => x.seasonNumber === n - 1);
        if (prev?.season.championId === champId) also.push(tell('{name} defended his title — {k} in all.', { name: champion.name, k: count(champion.titlesToDate) }));
        else also.push(tell('Title number {k} for {name}.', { k: count(champion.titlesToDate), name: champion.name }));
      }
      const roty = seasonAwards(asHistory(seasons), n, reg).find((a) => a.kind === 'rookie-of-the-year');
      if (roty) also.push(tell('Rookie of the Year: {name}, {rank} in the standings.', { name: roty.name, rank: ordinal(roty.value) }));
    }
    for (const f of s.season.fatalities ?? []) also.push(tell('{name} was lost in race {r}.', { name: f.name, r: f.raceNumber }));
    const retiredChamps = (s.offseason?.retirements ?? []).filter((r) => (reg.byId[r.driverId]?.career.championships ?? 0) > 0);
    for (const r of retiredChamps) also.push(tell('{name} retired that winter, a {k}-time champion.', { name: reg.byId[r.driverId].name, k: reg.byId[r.driverId].career.championships }));
    const lines = [...story.slice(0, 2), ...also];
    out.push({
      seasonNumber: n,
      complete,
      champion,
      eras: eras.filter((e) => n >= e.from && n <= e.to).map((e) => e.key),
      storyline: lines[0] ?? null,
      also: lines.slice(1, 5),
    });
  }
  return { eras, seasons: out };
}
